/**
 * Gemini prompt:
 *
 * I need a helper typescript function, for thresholding concurrent async requests:
 * - These requests can be identified by a provided parameters, to be considered as a request group
 * - Only when the first request in the group completes, the other requests can start
 * - Need a `ttl` parameter so the group cache will expire and be cleaned after a set seconds
 */

/**
 * A type for the function that extracts a unique group key from the request arguments.
 * This key is used to group concurrent requests together.
 */
type GroupKeyExtractor<TArgs extends any[]> = (
  ...args: TArgs
) => string | symbol;

/**
 * Defines the state for each request group, tracking the "gate" for subsequent requests.
 */
interface GroupState<T> {
  /** The promise of the very first request in the group. Subsequent requests wait for this to complete. */
  gatePromise: Promise<T>;
  /** A flag to indicate if the first request has completed successfully. */
  isFirstComplete: boolean;
}

/**
 * Creates a throttled function that manages concurrent async requests based on groups.
 *
 * For any given group, the first request is executed immediately, acting as a "gate".
 * Subsequent requests for the same group wait for the gate request to complete.
 *
 * - If the gate request SUCCEEDS, the gate is permanently opened, and all waiting and future
 * requests for that group are executed immediately.
 * - If the gate request FAILS, the next waiting request becomes the new gate.
 *
 * An optional TTL can be provided to automatically clean up a successful group's state
 * after a certain period of inactivity.
 *
 * @template T The return type of the async function.
 * @template TArgs The argument types of the async function.
 * @param asyncFn The asynchronous function to be throttled.
 * @param getGroupKey A function that takes the arguments of the async operation and returns a unique key for its group.
 * @param options Optional configuration.
 * @param options.ttl The time-to-live in milliseconds for a successful group's state.
 * @returns A new function that applies the thresholding logic to the async function.
 */
export function thresholdConcurrentRequests<T, TArgs extends any[]>(
  asyncFn: (...args: TArgs) => Promise<T>,
  getGroupKey: GroupKeyExtractor<TArgs>,
  options: { ttl?: number } = {}
): (...args: TArgs) => Promise<T> {
  const groups = new Map<string | symbol, GroupState<T>>();
  const { ttl } = options;

  return async (...args: TArgs): Promise<T> => {
    // This loop allows a request to re-evaluate the group's state after waiting.
    while (true) {
      const key = getGroupKey(...args);
      const groupState = groups.get(key);

      // Case 1: This is the first request for a group (or the previous gate failed).
      if (!groupState) {
        const firstRequestPromise = asyncFn(...args);

        const newState: GroupState<T> = {
          gatePromise: firstRequestPromise,
          isFirstComplete: false,
        };
        groups.set(key, newState);

        firstRequestPromise
          .then(() => {
            // On SUCCESS, the gate is permanently open for this group.
            newState.isFirstComplete = true;

            // If TTL is set, schedule cleanup after the specified time.
            if (typeof ttl === 'number') {
              setTimeout(() => {
                if (groups.get(key) === newState) {
                  groups.delete(key);
                }
              }, ttl);
            }
          })
          .catch(() => {
            // On FAILURE, remove the failed gate's state. This allows the next
            // waiting request to become the new gate.
            if (groups.get(key) === newState) {
              groups.delete(key);
            }
          });

        return firstRequestPromise;
      }

      // Case 2: A request for this group is already in-flight (gate is closed).
      if (!groupState.isFirstComplete) {
        try {
          // Wait for the current gate request to complete.
          // eslint-disable-next-line no-await-in-loop
          await groupState.gatePromise;
        } catch (err) {
          // The gate request failed. We don't throw here. Instead, we'll loop
          // again, and this request will become the new gate.
        }
        // After waiting, restart the loop to re-evaluate the state.
        // eslint-disable-next-line no-continue
        continue;
      }

      // Case 3: The gate for this group is already successfully open. Execute immediately.
      return asyncFn(...args);
    }
  };
}
